import configs from '~/env.json'
import { handleErrorToken } from '~/helpers/handleErrorToken'
import { CHANNEL_SOURCES } from '../helpers/constants'
import { isWebView } from '~/helpers/webView'

const FetchError = (message, response) =>
  Object.assign(new Error(message), {
    name: 'FetchError',
    response
  })

const isFetchError = (error) =>
  error instanceof Error && error?.name === 'FetchError' && typeof error?.response === 'object'

async function parseResponse(response) {
  const text = await response.text()
  try {
    return text ? JSON.parse(text) : null
  } catch {
    return text
  }
}

const normalizeParams = (params = {}) => {
  const filteredParams = Object.entries(params).reduce((accumulator, [key, value]) => {
    if (value !== undefined && value !== null) {
      accumulator[key] = value
    }
    return accumulator
  }, {})

  return new URLSearchParams(filteredParams).toString()
}

const joinUrl = (base, path) => `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`

const buildRequestUrl = ({ baseURL, url, params }) => {
  const queryString = normalizeParams(params)
  const separator = url.includes('?') ? '&' : '?'
  const pathWithQuery = queryString ? `${url}${separator}${queryString}` : url

  return joinUrl(baseURL, pathWithQuery)
}

const getAccessToken = () => {
  const search = window.location.search
  const params = new URLSearchParams(search)
  return params.get('accessToken') || localStorage.getItem('accessToken')
}

const buildHeaders = ({ headers = {} }) => {
  const requestHeaders = { ...headers }
  const accessToken = getAccessToken()

  if (isWebView()) {
    requestHeaders['x-channel-source'] = CHANNEL_SOURCES.APP
  }

  const hasAuthHeader = 'Authorization' in requestHeaders || 'authorization' in requestHeaders
  if (accessToken && !hasAuthHeader) {
    requestHeaders.Authorization = `Bearer ${accessToken}`
  }

  return requestHeaders
}

const createFetchOptions = ({ method, headers, data }) => {
  const fetchOptions = {
    method,
    headers
  }

  if (data === undefined) {
    return fetchOptions
  }

  if (data instanceof FormData) {
    fetchOptions.body = data
    return fetchOptions
  }

  fetchOptions.body = JSON.stringify(data)
  fetchOptions.headers = {
    ...headers,
    'Content-Type': 'application/json'
  }

  return fetchOptions
}

async function apiFetch(method, url, data, options = {}, isRetry = false) {
  const baseURL = options.baseURL || configs?.REACT_APP_API
  const headers = buildHeaders({ headers: options.headers })

  const fullUrl = buildRequestUrl({
    baseURL,
    url,
    params: options.params
  })

  try {
    const fetchOptions = createFetchOptions({
      method,
      headers,
      data
    })
    const response = await fetch(fullUrl, fetchOptions)
    const responseData = await parseResponse(response)

    if ([401, 403].includes(response.status) && !isRetry) {
      const refreshToken = localStorage.getItem('refreshToken')
      try {
        const refreshResponse = await apiFetch('POST', '/refreshToken', { refreshToken }, {}, true)
        const { accessToken, refreshToken: newRefreshToken } =
          (refreshResponse?.data?.data && refreshResponse.data.data[0]) || {}

        if (!accessToken || !newRefreshToken) {
          throw FetchError('Erro ao atualizar sessão', {
            status: refreshResponse?.status || response.status,
            data: refreshResponse?.data
          })
        }

        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', newRefreshToken)

        return apiFetch(
          method,
          url,
          data,
          {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${accessToken}`
            }
          },
          true
        )
      } catch (error) {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        handleErrorToken()

        return {
          status: 200,
          data: {
            success: false,
            message: 'Erro com a identificação do usuário',
            errors: ['Sua sessão expirou. Você será redirecionado para a tela inicial.']
          }
        }
      }
    }

    if (!response.ok) {
      console.error(`Erro na requisição (status ${response.status})`)
      throw FetchError(responseData?.message || 'Erro na requisição', {
        status: response.status,
        data: responseData
      })
    }

    return { status: response.status, data: responseData }
  } catch (error) {
    if (isFetchError(error)) {
      throw error
    }

    throw FetchError(error.message || 'Erro na requisição', {
      status: 0,
      data: null
    })
  }
}

const fetchClient = {
  get: (url, options = {}) => apiFetch('GET', url, undefined, options),
  post: (url, data, options = {}) => apiFetch('POST', url, data, options)
}

export const fetchJson = async (url, options = {}) => {
  const response = await fetch(url, options)
  const parsedData = await parseResponse(response)

  if (!response.ok) {
    throw FetchError(parsedData?.message || 'Erro na requisição', {
      status: response.status,
      data: parsedData
    })
  }

  return { status: response.status, data: parsedData }
}

export default fetchClient
